import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) throw new Error(body?.message || 'EPC request failed');
  return body.data;
}

export const epcService = {
  getRuntime: () => unwrap<Record<string, unknown>>(api.get('/epc/runtime')),
  getDisciplines: () => unwrap<unknown[]>(api.get('/epc/engineering/disciplines')),
  getDrawings: (projectId?: string) =>
    unwrap<unknown[]>(
      api.get('/epc/engineering/drawings', { params: projectId ? { projectId } : {} })
    ),
  createDrawing: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/engineering/drawings', payload)),
  addRevision: (drawingId: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/epc/engineering/drawings/${drawingId}/revisions`, payload)),
  getTransmittals: (projectId?: string) =>
    unwrap<unknown[]>(
      api.get('/epc/engineering/transmittals', { params: projectId ? { projectId } : {} })
    ),
  createTransmittal: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/engineering/transmittals', payload)),
  getTechnicalQueries: (projectId?: string) =>
    unwrap<unknown[]>(
      api.get('/epc/engineering/technical-queries', { params: projectId ? { projectId } : {} })
    ),
  createTechnicalQuery: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/engineering/technical-queries', payload)),
  getDesignChanges: (projectId?: string) =>
    unwrap<unknown[]>(
      api.get('/epc/engineering/design-changes', { params: projectId ? { projectId } : {} })
    ),
  createDesignChange: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/engineering/design-changes', payload)),
  getMaterialRequisitions: (projectId?: string) =>
    unwrap<unknown[]>(
      api.get('/epc/engineering/material-requisitions', {
        params: projectId ? { projectId } : {},
      })
    ),
  createMaterialRequisition: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/engineering/material-requisitions', payload)),
  getProgress: (projectId?: string) =>
    unwrap<Record<string, unknown>>(
      api.get('/epc/engineering/progress', { params: projectId ? { projectId } : {} })
    ),
  getWbsStructures: (projectId: string) =>
    unwrap<unknown[]>(api.get('/epc/wbs/structures', { params: { projectId } })),
  bootstrapWbs: (projectId: string) =>
    unwrap<unknown>(api.post('/epc/wbs/bootstrap', { projectId })),
  getWbsNodes: (structureId: string) =>
    unwrap<unknown[]>(api.get('/epc/wbs/nodes', { params: { structureId } })),
  getWbsLayers: () => unwrap<unknown[]>(api.get('/epc/wbs/layers')),
  createWbsNode: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs/nodes', body)),
  patchWbsNode: (nodeId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs/nodes/${nodeId}`, body)),
  deleteWbsNode: (nodeId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs/nodes/${nodeId}`)),
  getMilestones: (projectId?: string) =>
    unwrap<unknown[]>(
      api.get('/epc/planning/milestones', { params: projectId ? { projectId } : {} })
    ),

  // Program 12.1 R2 — Construction Execution Platform
  getCepRuntime: () => unwrap<Record<string, unknown>>(api.get('/epc/execution/runtime')),
  getAwpPaths: (projectId: string) =>
    unwrap<unknown[]>(api.get('/epc/awp/path-of-construction', { params: { projectId } })),
  getAwpReleases: (projectId: string) =>
    unwrap<unknown[]>(api.get('/epc/awp/releases', { params: { projectId } })),
  convertAwpFromContract: (projectId: string, contractId?: string) =>
    unwrap<unknown>(
      api.post('/epc/awp/projects/convert-from-contract', { projectId, contractId })
    ),
  getExecutionPackages: (projectId?: string) =>
    unwrap<unknown[]>(
      api.get('/epc/execution-packages', { params: projectId ? { projectId } : {} })
    ),
  createExecutionPackage: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/execution-packages', payload)),
  getExecutionPackage: (id: string) =>
    unwrap<unknown>(api.get(`/epc/execution-packages/${encodeURIComponent(id)}`)),
  patchExecutionPackage: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/execution-packages/${encodeURIComponent(id)}`, payload)),
  linkPackageDrawing: (packageId: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(
      api.post(`/epc/execution-packages/${encodeURIComponent(packageId)}/drawings`, payload)
    ),
  unlinkPackageDrawing: (packageId: string, drawingId: string) =>
    unwrap<unknown>(
      api.delete(
        `/epc/execution-packages/${encodeURIComponent(packageId)}/drawings/${encodeURIComponent(drawingId)}`
      )
    ),
  addPackageDependency: (packageId: string, dependsOnPackageId: string) =>
    unwrap<unknown>(
      api.post(`/epc/execution-packages/${encodeURIComponent(packageId)}/dependencies`, {
        dependsOnPackageId,
      })
    ),
  removePackageDependency: (packageId: string, dependsOnPackageId: string) =>
    unwrap<unknown>(
      api.delete(
        `/epc/execution-packages/${encodeURIComponent(packageId)}/dependencies/${encodeURIComponent(dependsOnPackageId)}`
      )
    ),
  getWorkAreas: (projectId?: string) =>
    unwrap<unknown[]>(api.get('/epc/work-areas', { params: projectId ? { projectId } : {} })),
  createWorkArea: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/work-areas', payload)),
  getWorkFronts: (projectId?: string) =>
    unwrap<unknown[]>(api.get('/epc/work-fronts', { params: projectId ? { projectId } : {} })),
  createWorkFront: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/work-fronts', payload)),
  refreshWorkFront: (id: string) =>
    unwrap<unknown>(api.post(`/epc/work-fronts/${encodeURIComponent(id)}/refresh-readiness`, {})),
  getCepProgress: (projectId?: string, mode?: string) =>
    unwrap<Record<string, unknown>>(
      api.get('/epc/progress', {
        params: {
          ...(projectId ? { projectId } : {}),
          ...(mode ? { mode } : {}),
        },
      })
    ),
  getPackageDailyLogs: (packageId: string) =>
    unwrap<unknown[]>(
      api.get(`/epc/execution-packages/${encodeURIComponent(packageId)}/daily-logs`)
    ),
  createPackageDailyLog: (packageId: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(
      api.post(`/epc/execution-packages/${encodeURIComponent(packageId)}/daily-logs`, payload)
    ),
  getCepConstraints: (projectId?: string) =>
    unwrap<unknown[]>(
      api.get('/epc/constraints', { params: projectId ? { projectId } : {} })
    ),
  createConstraint: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/constraints', payload)),
  patchConstraint: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/constraints/${encodeURIComponent(id)}`, payload)),
  getCepManpower: (projectId?: string) =>
    unwrap<unknown[]>(api.get('/epc/manpower', { params: projectId ? { projectId } : {} })),
  createManpower: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/manpower', payload)),
  patchManpower: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/manpower/${encodeURIComponent(id)}`, payload)),
  getCepEquipment: (projectId?: string) =>
    unwrap<unknown[]>(api.get('/epc/equipment', { params: projectId ? { projectId } : {} })),
  createEquipment: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/equipment', payload)),
  patchEquipment: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/equipment/${encodeURIComponent(id)}`, payload)),
  getCepProductivity: (projectId?: string) =>
    unwrap<Record<string, unknown>>(
      api.get('/epc/productivity', { params: projectId ? { projectId } : {} })
    ),
  getCepPermits: (projectId?: string) =>
    unwrap<unknown[]>(api.get('/epc/permits', { params: projectId ? { projectId } : {} })),
  getPermit: (id: string) =>
    unwrap<unknown>(api.get(`/epc/permits/${encodeURIComponent(id)}`)),
  createPermit: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/permits', payload)),
  patchPermit: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/permits/${encodeURIComponent(id)}`, payload)),
  submitPermitForApproval: (id: string) =>
    unwrap<unknown>(api.post(`/epc/permits/${encodeURIComponent(id)}/submit-for-approval`, {})),
  getCepPhotos: (projectId?: string) =>
    unwrap<unknown[]>(api.get('/epc/photos', { params: projectId ? { projectId } : {} })),
  createPhoto: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/photos', payload)),
  uploadPhoto: (payload: {
    file: File;
    projectId: string;
    packageId: string;
    stage?: string;
    caption?: string;
    capturedAt?: string;
  }) => {
    const fd = new FormData();
    fd.append('file', payload.file);
    fd.append('projectId', payload.projectId);
    fd.append('packageId', payload.packageId);
    if (payload.stage) fd.append('stage', payload.stage);
    if (payload.caption) fd.append('caption', payload.caption);
    if (payload.capturedAt) fd.append('capturedAt', payload.capturedAt);
    return unwrap<unknown>(api.post('/epc/photos', fd));
  },
  deletePhoto: (id: string) =>
    unwrap<unknown>(api.delete(`/epc/photos/${encodeURIComponent(id)}`)),
  getCepFieldDocuments: (projectId?: string, opts?: { packageId?: string; docType?: string }) =>
    unwrap<unknown[]>(
      api.get('/epc/field-documents', {
        params: {
          ...(projectId ? { projectId } : {}),
          ...(opts?.packageId ? { packageId: opts.packageId } : {}),
          ...(opts?.docType ? { docType: opts.docType } : {}),
        },
      })
    ),
  createFieldDocument: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/field-documents', payload)),
  patchFieldDocument: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/field-documents/${encodeURIComponent(id)}`, payload)),
  getCepDelays: (projectId?: string) =>
    unwrap<unknown[]>(api.get('/epc/delays', { params: projectId ? { projectId } : {} })),
  createDelay: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/delays', payload)),
  patchDelay: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/delays/${encodeURIComponent(id)}`, payload)),
  getCepDashboards: (projectId?: string) =>
    unwrap<Record<string, unknown>>(
      api.get('/epc/dashboards', { params: projectId ? { projectId } : {} })
    ),
  getCepWeather: (projectId?: string) =>
    unwrap<unknown[]>(api.get('/epc/weather', { params: projectId ? { projectId } : {} })),
  createWeather: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/weather', payload)),
  patchWeather: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/weather/${encodeURIComponent(id)}`, payload)),
  getCepFieldAi: (projectId?: string, opts?: { packageId?: string; kind?: string; list?: boolean }) =>
    unwrap<Record<string, unknown>>(
      api.get('/epc/field-ai', {
        params: {
          ...(projectId ? { projectId } : {}),
          ...(opts?.packageId ? { packageId: opts.packageId } : {}),
          ...(opts?.kind ? { kind: opts.kind } : {}),
          ...(opts?.list ? { list: 1 } : {}),
        },
      })
    ),
  generateFieldAi: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/field-ai', payload)),
  getCepDigitalTwin: (projectId?: string) =>
    unwrap<Record<string, unknown>>(
      api.get('/epc/digital-twin', { params: projectId ? { projectId } : {} })
    ),
  simulateDigitalTwin: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/digital-twin/simulate', payload)),
  promoteDigitalTwin: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/digital-twin/promote', payload)),
};
